import gql from 'graphql-tag';
import { cloneDeep } from 'lodash';

// @ts-ignore
import OrganizationActions from 'actions/OrganizationActions';
// @ts-ignore
import RelationshipActions from 'actions/RelationshipActions';
import ToastActions from 'actions/ToastActions';
// @ts-ignore
import { touch } from 'actions/touchActions';
import GroupMemberships from 'collections/GroupMemberships';
import Groups from 'collections/Groups';
// @ts-ignore
import RootGroupMemberships from 'collections/RootGroupMemberships';
import { TOUCH_KEY as NAV_TOUCH_KEY } from 'components/NavigationList/NavigationList';
import { ActionTypes as GroupMembershipActionTypes } from 'constants/GroupMembershipConstants';
import Group, { GROUP_MEMBER_PAGE_SIZE, LoadGroupMembershipsParams } from 'entities/Group';
import { rootGroup } from 'entities/RootGroup';
import { ActionTypes as GroupActionTypes } from 'groups/constants/GroupConstants';
import * as GroupPromises from 'groups/lib/GroupPromises';
// @ts-ignore
import { dataIdFromObject } from 'lib/apollo/cache/memory';
import { getApolloClientSingleton } from 'lib/apollo/client/browser';
import { dispatcher } from 'lib/Dispatcher';
import API from 'lib/RemindAPI';

import { UserGroupsQuery } from 'groups/queries/__generated__/UserGroupsQuery';
import { Backbone$Attrs, Backbone$Id } from 'types/typescript/Backbone';
import { LoadSpecificGroups } from './queries/__generated__/LoadSpecificGroups';

import classesDefaultsFragment from 'groups/fragments/classesDefaultsFragment.gql';
import USER_GROUPS_QUERY from 'groups/queries/UserGroupsQuery.gql';
import LOAD_SPECIFIC_GROUPS_QUERY from './queries/LoadSpecificGroupsQuery.gql';

/* This is to update the
 * navigation item interface fragments.
 *
 */
function updateApolloGroup(group: { uuid: string, class_name: string, name: string, group_avatar: { id: string, svg_url?: string } | null }) {
  const classFragment = gql`
    fragment class on Class {
      displayName
      class_name
      name
      group_avatar {
        uuid
        id
        svgUrl
      }
    }
  `;

  const groupAvatarFields = group.group_avatar
    ? {
      __typename: 'GroupAvatar',
      id: group.group_avatar.id,
      svgUrl: group.group_avatar.svg_url,
    }
    : null;

  getApolloClientSingleton().writeFragment({
    id: dataIdFromObject({ __typename: 'Class', uuid: group.uuid }),
    fragment: classFragment,
    variables: { uuid: group.uuid },
    data: {
      __typename: 'Class',
      displayName: group.class_name,
      class_name: group.class_name,
      name: group.name,
      group_avatar: groupAvatarFields,
    },
  });
}

async function loadAllGroups(forceFetch: boolean) {
  const data = await API.execute<UserGroupsQuery>({ query: USER_GROUPS_QUERY, forceFetch });

  return data && data.me && data.me.classes;
}

async function loadSpecificGroups(requestedClassUuids: string[], forceFetch: boolean) {
  // we had a bug where we were sending null uuids for LoadSpecificGroups
  // https://rollbar.com/Remind/r101-apollo/items/43656/
  // so now we filter and report client-side
  const classUuids = requestedClassUuids.filter(Boolean);
  if (classUuids.length !== requestedClassUuids.length) {
    console.warn({ requestedClassUuids });
    console.error('loadSpecificGroups got classUuids containing empty vals');
  }
  const variables = { classUuids };
  const data = await API.execute<LoadSpecificGroups>({ query: LOAD_SPECIFIC_GROUPS_QUERY, variables, forceFetch });
  return data.classes;
}


const GroupActions = {
  // TODO kill this action that's not an action (doesn't even dispatch)
  searchGroupByCode(groupCode: string): Promise<Group | void | null> {
    return Group.fetchByCode(groupCode).catch(() => undefined);
  },

  async loadGroups(classUuids?: string[], forceFetch: boolean = true): Promise<Group[]> {
    const classes = classUuids
      ? await loadSpecificGroups(classUuids, forceFetch)
      : await loadAllGroups(forceFetch);

    const allMine = classUuids === undefined;
    const groups: Group[] = (classes || [])
      .filter(Boolean) // GraphQL returns null for unavailable values
      .map(group => Group.fromJSON(cloneDeep(group))); // clone to separate Apollo from Backbone data

    dispatcher.dispatch({
      type: GroupActionTypes.LIST_GROUPS,
      groups,
      allMine,
    });

    return groups;
  },

  resetStore(): void {
    dispatcher.dispatch({
      type: GroupActionTypes.RESET_STORE,
    });
  },

  loadPotentialOwners(group: Group, page: number = 1, perPage: number = 20) {
    return group.loadPotentialOwners(page, perPage).then(potentialOwners => {
      /* todo dispatch event, unnecessary for now */
      return potentialOwners;
    });
  },

  async loadRootGroupMemberships(
    params: LoadGroupMembershipsParams = { page: 1, perPage: GROUP_MEMBER_PAGE_SIZE }
  ): Promise<GroupMemberships> {
    const data = {
      page: 1,
      perPage: GROUP_MEMBER_PAGE_SIZE,
      ...params,
    };

    const members = await RootGroupMemberships.fetch({ data });

    const groupMembers = members.getGroupMemberships();

    dispatcher.dispatch({
      type: GroupMembershipActionTypes.LOAD_CLASS_MEMBERS,
      members: groupMembers,
      group: rootGroup,
      membersRequested: data.perPage,
      query: data.query,
    });

    return groupMembers;
  },

  async loadGroupMemberships(
    group: Group,
    params: LoadGroupMembershipsParams = { page: 1, perPage: GROUP_MEMBER_PAGE_SIZE }
  ): Promise<GroupMemberships> {
    if (group.isRoot()) {
      return this.loadRootGroupMemberships(params);
    }

    const members = await group.loadGroupMemberships(params);

    dispatcher.dispatch({
      type: GroupMembershipActionTypes.LOAD_CLASS_MEMBERS,
      membersRequested: params.perPage || GROUP_MEMBER_PAGE_SIZE,
      query: params.query || '',
      members,
      group,
    });

    return members;
  },

  async loadArchivedGroups(): Promise<void> {
    const groups = await Groups.fetchArchived();

    dispatcher.dispatch({
      type: GroupActionTypes.LIST_ARCHIVED_GROUPS,
      groups,
    });
  },

  addSubscribers(group: Group, subscribers: Array<{ id: Backbone$Id }>) {
    return group.addSubscribers(subscribers).then(members => {
      // TODO: This is temporary. This is needed to ensure visible views get the correct state since
      // subscribing to a group no longer gets a Subscription object.
      const relationshipUpdates = members.map(member =>
        RelationshipActions.updateRelationship(member.getUserId())
      );
      return Promise.all(relationshipUpdates);
    });
  },

  async promoteGroupMemberships(group: Group, memberIds: Backbone$Id[]) {
    const members = await group.promoteGroupMemberships(memberIds);
    dispatcher.dispatch({
      type: GroupMembershipActionTypes.PROMOTED_CLASS_MEMBERS,
      members,
      group,
    });
    return members;
  },

  async resignOwnership(group: Group) {
    await group.resignOwnership();

    dispatcher.dispatch({
      type: GroupActionTypes.RESIGN_OWNERSHIP,
      group,
    });

    loadAllGroups(true);
  },

  removeMember(group: Group, subscriber: { id: Backbone$Id }) {
    return group.removeMember(subscriber).then(() => {
      dispatcher.dispatch({
        type: GroupMembershipActionTypes.REMOVE_SUBSCRIBER,
        subscriber,
      });

      // TODO: This is temporary. This is needed to ensure visible views get the correct state since
      // subscribing to a group no longer gets a Subscription object.
      return RelationshipActions.updateRelationship(subscriber.id);
    });
  },

  async create(groupToCreate: Group): Promise<Group> {
    const group = await groupToCreate.create();
    touch(NAV_TOUCH_KEY);

    dispatcher.dispatch({
      type: GroupActionTypes.CREATE_GROUP,
      groups: [group],
    });

    loadAllGroups(true);

    return group;
  },

  patch(groupToPatch: Group, attributes: Backbone$Attrs, toastText?: string, showToast: boolean = true) {
    return groupToPatch.patch(attributes).then(() => {
      if (attributes.organization_id !== undefined) {
        dispatcher.dispatch({
          type: GroupActionTypes.CHANGE_ORG,
          groupUuid: groupToPatch.getUuid(),
        });
      }

      if (showToast) {
        ToastActions.showSuccessToast(toastText || 'Saved');
      }
      const group = groupToPatch.toJSON();
      const groupJSON = {
        uuid: groupToPatch.getUuid(),
        class_name: groupToPatch.getName(),
        name: groupToPatch.getCodeOrError(),
        group_avatar: groupToPatch.getAvatar() || null,
      }

      updateApolloGroup(groupJSON);

      if ('organization_id' in attributes) {
        touch(NAV_TOUCH_KEY);
      }

      dispatcher.dispatch({
        type: GroupActionTypes.UPDATE_GROUP,
        group: groupToPatch,
      });

      return groupToPatch;
    });
  },

  setGroupsAllowedToStartChat(groupsToAllow: Groups) {
    return Groups.setGroupsAllowedToStartChat(groupsToAllow).then(groups => {
      dispatcher.dispatch({
        type: GroupActionTypes.GROUPS_UPDATED,
        groups,
      });
    });
  },

  subscribeToGroup(groupToSubscribe: Group): Promise<Group> {
    return groupToSubscribe.subscribe().then(
      group => {
        touch(NAV_TOUCH_KEY);
        const org = group.getOrganization();
        if (org) {
          OrganizationActions.getOrLoadOrganization(org.id);
        }

        const owners = group.getOwners() as GroupMemberships;
        owners.map(() => undefined);

        // TODO: This is temporary. This is needed to ensure visible views get the correct state
        // since subscribing to a group no longer gets a Subscription object.
        const relationshipUpdates = (group.getOwners() as GroupMemberships)
          .map(member => RelationshipActions.updateRelationship(member.getUserId()));
        return Promise.all(relationshipUpdates).then(() => {
          dispatcher.dispatch({
            type: GroupActionTypes.SUBSCRIBED,
            group,
          });
          return group;
        });
      },
      e => {
        if (e.code === 'duplicate_membership') {
          console.warn(e, e && e.stack);
          return groupToSubscribe;
        } else {
          throw e;
        }
      }
    );
  },

  subscribeToGroupWithCode(code: string): Promise<Group> {
    return this.subscribeToGroup(Group.createGroupFromCode(code));
  },

  unsubscribeFromGroup(group: Group) {
    return group.unsubscribe().then(() => {
      touch(NAV_TOUCH_KEY);
      // TODO: This is temporary. This is needed to ensure visible views get the correct state since
      // subscribing to a group no longer gets a Subscription object.
      const relationshipUpdates = (group.getOwners() as GroupMemberships)
        .map(member => RelationshipActions.updateRelationship(member.getUserId()));
      return Promise.all(relationshipUpdates).then(() => {
        dispatcher.dispatch({
          type: GroupActionTypes.UNSUBSCRIBED,
          group,
        });
        return group;
      });
    });
  },

  removeGroup(group: Group) {
    dispatcher.dispatch({
      type: GroupActionTypes.DELETE_GROUP,
      group,
    });
  },

  async archiveGroup(groupToArchive: Group): Promise<Group> {
    const group = await groupToArchive.archive();
    touch(NAV_TOUCH_KEY);

    dispatcher.dispatch({
      type: GroupActionTypes.ARCHIVE,
      group,
    });

    loadAllGroups(true);

    return group;
  },

  async reuseGroup(
    archivedGroup: Group,
    organizationId?: Backbone$Id,
    classCode?: string
  ): Promise<Group> {
    const group = await archivedGroup.reuse(organizationId, classCode);
    touch(NAV_TOUCH_KEY);

    dispatcher.dispatch({
      type: GroupActionTypes.REUSE,
      group,
      archivedGroup,
    });

    await GroupPromises.loadGroupByCode(classCode || archivedGroup.getCodeOrError(), true);

    return group;
  },

  async archiveAndReuseGroup(
    groupToArchive: Group,
    organizationId?: Backbone$Id,
    classCode?: string
  ): Promise<Group> {
    const archivedGroup = await groupToArchive.archive();
    const group = await archivedGroup.reuse(organizationId, classCode);
    touch(NAV_TOUCH_KEY);

    dispatcher.dispatch({
      type: GroupActionTypes.ARCHIVE_AND_REUSE,
      group,
      archivedGroup,
    });

    await GroupPromises.loadGroupByCode(classCode || archivedGroup.getCodeOrError(), true);
    loadAllGroups(true);

    return group;
  },

  restoreGroup(
    groupToRestore: Group,
    organizationId?: Backbone$Id,
    classCode?: string
  ): Promise<Group> {
    const group = groupToRestore.unarchive(organizationId, classCode);
    touch(NAV_TOUCH_KEY);

    dispatcher.dispatch({
      type: GroupActionTypes.RESTORE,
      group,
    });

    return group;
  },

  addToGroupsJoinedDuringSetup(groupJoined: Group) {
    dispatcher.dispatch({
      type: GroupActionTypes.JOIN_GROUP_DURING_SETUP,
      group: groupJoined,
    });
  },

  setGroupCreatedDuringSetup(groupCreated: Group) {
    dispatcher.dispatch({
      type: GroupActionTypes.CREATE_GROUP_DURING_SETUP,
      group: groupCreated,
    });
  },

  clearGroupCreatedDuringSetup() {
    dispatcher.dispatch({
      type: GroupActionTypes.CLEAR_GROUP_CREATED_DURING_SETUP,
    });
  },

  clearGroupsJoinedDuringSetup() {
    dispatcher.dispatch({
      type: GroupActionTypes.CLEAR_GROUPS_JOINED_DURING_SETUP,
    });
  },
};

export default GroupActions;
